/**
 * Clinical Data Service
 * Carga datos clínicos desde archivos JSON estáticos por location.
 * Usa dynamic import para cargar solo los datos de la location seleccionada.
 *
 * Los JSONs se encuentran en: src/pages/Main/Temp/DataDashClinical/{locationKey}/
 * Cada location tiene 6 archivos: summary.json, alerts.json, patients.json,
 * care_gaps.json, coding.json, recall.json
 *
 * Migración futura: Reemplazar imports estáticos por llamadas API.
 */

// Cache de locations (se carga una sola vez)
let locationsCache = null;

/**
 * Obtiene la lista de locations disponibles.
 *
 * Ejemplo de respuesta:
 * [
 *   {
 *     "key": "fort_myers",
 *     "address": "3900 BROADWAY STE C UNIT 13, Fort Myers, FL - 33901, Lee",
 *     "patients": 2017,
 *     "pcpCount": 6,
 *     "pcps": [{ "npi": "1508093154", "name": "SMITH, REYNALDO PEZZOTTI MD", ... }]
 *   }
 * ]
 *
 * @returns {Promise<Array>} Lista de locations
 */
export async function getLocations() {
  if (locationsCache) return locationsCache;
  const module = await import("../../../pages/Main/Temp/DataDashClinical/locations.json");
  locationsCache = module.default?.locations || module.locations || [];
  return locationsCache;
}

/**
 * Obtiene el summary (KPIs, PCPs, health breakdown) de una location.
 *
 * @param {string} locationKey - Key de la location (e.g., "fort_myers")
 *
 * Ejemplo de respuesta:
 * {
 *   "meta": { "locationKey": "fort_myers", "address": "...", "sourceDate": "2026-02-13" },
 *   "pcps": [{ "npi": "...", "name": "...", "patients": 1147 }],
 *   "kpis": { "assignedPatients": 2017, "highCost": 104, ... },
 *   "healthStatusBreakdown": { "Well": 928, "Acute": 438, ... },
 *   "visitTimeframeBreakdown": { "12+ Months": 1298, ... }
 * }
 *
 * @returns {Promise<Object>} Summary data
 */
export async function getSummary(locationKey) {
  const module = await import(
    `../../../pages/Main/Temp/DataDashClinical/${locationKey}/summary.json`
  );
  return module.default || module;
}

/**
 * Obtiene las alertas ADT (Admitted/Discharged/Emergency) de una location.
 *
 * @param {string} locationKey - Key de la location
 *
 * Ejemplo de respuesta:
 * {
 *   "counts": { "admitted": 1, "discharged": 27, "emergency": 92 },
 *   "admitted": [{ "cipId": ..., "name": "...", "facility": "...", "admitDate": "...", "lace": 11 }],
 *   "discharged": [...],
 *   "emergency": [...]
 * }
 *
 * @returns {Promise<Object>} Alerts data
 */
export async function getAlerts(locationKey) {
  const module = await import(
    `../../../pages/Main/Temp/DataDashClinical/${locationKey}/alerts.json`
  );
  return module.default || module;
}

/**
 * Obtiene la lista completa de pacientes de una location.
 *
 * @param {string} locationKey - Key de la location
 *
 * Ejemplo de respuesta (array de pacientes):
 * [
 *   {
 *     "cipId": 6250434397,
 *     "name": "ABDALA VIVIEROS, MARIA",
 *     "age": 49,
 *     "gender": "F",
 *     "healthStatus": "Chronic",
 *     "lastVisit": "12+ Months",
 *     "openGaps": 2,
 *     "conditions": "Diabetes",
 *     "erEvents": 1,
 *     "medications": 0,
 *     "currentInpatient": false,
 *     "recentlyDischarged": false,
 *     "newMember": false,
 *     "pcpName": "SMITH, REYNALDO PEZZOTTI MD"
 *   }
 * ]
 *
 * @returns {Promise<Array>} Patients array
 */
export async function getPatients(locationKey) {
  const module = await import(
    `../../../pages/Main/Temp/DataDashClinical/${locationKey}/patients.json`
  );
  return module.default || module;
}

/**
 * Obtiene datos de Care Gaps de una location.
 *
 * @param {string} locationKey - Key de la location
 *
 * Ejemplo de respuesta:
 * {
 *   "totals": { "open": 1803, "closed": 653, "submitted": 0, "totalPatients": 2017, "nonCompliantCount": 585 },
 *   "measures": [{ "name": "Breast Cancer Screening", "open": 250, "closed": 120, ... }],
 *   "topPatientsByGaps": [{ "cipId": ..., "name": "...", "openGaps": 5 }]
 * }
 *
 * @returns {Promise<Object>} Care gaps data
 */
export async function getCareGaps(locationKey) {
  const module = await import(
    `../../../pages/Main/Temp/DataDashClinical/${locationKey}/care_gaps.json`
  );
  return module.default || module;
}

/**
 * Obtiene datos de Coding Opportunities de una location.
 *
 * @param {string} locationKey - Key de la location
 *
 * Ejemplo de respuesta:
 * {
 *   "totals": { "total": 729, "notAddressed": 684, "addressed": 45, "totalPatients": 475 },
 *   "conditionTypes": { "Dropped": 400, "Suspect": 200, "Pharmacy": 129 },
 *   "topHCCs": [{ "hccId": "HCC 19", "description": "Diabetes...", "total": 120, "notAddressed": 100 }],
 *   "topPatientsByOpps": [{ "cipId": ..., "name": "...", "opportunities": 8 }]
 * }
 *
 * @returns {Promise<Object>} Coding data
 */
export async function getCoding(locationKey) {
  const module = await import(
    `../../../pages/Main/Temp/DataDashClinical/${locationKey}/coding.json`
  );
  return module.default || module;
}

/**
 * Obtiene datos de Recall (pacientes sin visita reciente) de una location.
 *
 * @param {string} locationKey - Key de la location
 *
 * Ejemplo de respuesta:
 * {
 *   "distribution": { "12+ Months": 1298, "9 Months": 132, "6 Months": 194 },
 *   "contactability": { "withPhone": 900, "withEmail": 450, "withMobile": 600 },
 *   "totalRecallEligible": 1624,
 *   "recallList": [{ "cipId": ..., "name": "...", "timeSinceLastVisit": "12+ Months", ... }]
 * }
 *
 * @returns {Promise<Object>} Recall data
 */
export async function getRecall(locationKey) {
  const module = await import(
    `../../../pages/Main/Temp/DataDashClinical/${locationKey}/recall.json`
  );
  return module.default || module;
}

/**
 * Carga todos los 6 datasets de una location en paralelo.
 *
 * @param {string} locationKey - Key de la location (e.g., "fort_myers")
 *
 * @returns {Promise<Object>} Objeto con { summary, alerts, patients, careGaps, coding, recall }
 *
 * Posibles errores:
 * - Si el locationKey no existe, el dynamic import fallará con un error de módulo no encontrado
 */
export async function getLocationData(locationKey) {
  const [summary, alerts, patients, careGaps, coding, recall] =
    await Promise.all([
      getSummary(locationKey),
      getAlerts(locationKey),
      getPatients(locationKey),
      getCareGaps(locationKey),
      getCoding(locationKey),
      getRecall(locationKey),
    ]);

  return { summary, alerts, patients, careGaps, coding, recall };
}
